import { calculateAge } from '../general-utilities/general-utilities';
import { IResumeData } from '../interfaces/common.interface';
export interface BestResponse {
  response: null | (() => string);
  score: number;
}

interface Intent {
  id: string;
  keywords: string[];
  handler: () => string;
}

export class QnAService {
  private _resumeData: IResumeData | null = null;

  public updateResumeData(resumeData: IResumeData) {
    this._resumeData = resumeData;
  }

  private get intents(): Intent[] {
    return [
      {
        id: "about",
        keywords: ["about", "who are you", "summary", "profile", "yourself"],
        handler: () =>
          this._resumeData?.aboutMe?.short_summary?.replace("#SKILL", this._resumeData?.aboutMe?.skill!)
            ?.replace("#YEAR", this.getExperienceYears())!,
      },

      {
        id: "hello",
        keywords: ["hello", "hey"],
        handler: () => "Hello! How can I help you today?",
      },

      {
        id: "name",
        keywords: ["name"],
        handler: () => `My name is ${this._resumeData?.aboutMe?.name}.`,
      },

      {
        id: "address",
        keywords: ["address", "live"],
        handler: () => `I live in ${this._resumeData?.aboutMe?.contactInfo?.Address}.`,
      },

      {
        id: "total_exp",
        keywords: ["total", "overall", "experience"],
        handler: () => `I'm having ${this.getExperienceYears()} years of experience.`,
      },

      {
        id: "age",
        keywords: ["age", "how old"],
        handler: () => `I'm ${calculateAge("1989-08-01")} years old`,
      },

      {
        id: "designation",
        keywords: ["designation", "role", "job"],
        handler: () => this._resumeData?.aboutMe?.designation!,
      },

      {
        id: "currentCompany",
        keywords: ["currently working", "working", "company"],
        handler: () => `I'm currently working at ${this._resumeData?.employmentHistory?.[1]?.organisation} as ${this._resumeData?.employmentHistory?.[1]?.designation}`,
      },

      {
        id: "skills",
        keywords: ["skill", "technology", "tech stack", "stack"],
        handler: () => 'My core skills include React, Nodejs, AWS, TypeScript, Redux, and system design.',
      },

      {
        id: "frontend",
        keywords: ["frontend", "front end", "ui", "react"],
        handler: () => "On the frontend, I specialize in React, TypeScript, Redux Toolkit, and performance optimization.",
      },

      {
        id: "backend",
        keywords: ["backend", "server", "node"],
        handler: () => "On the backend, I build scalable APIs using Nodejs, Express, AWS Lambda, Dynamo DB and Mongo DB.",
      },

      {
        id: "cloud",
        keywords: ["aws", "cloud", "serverless"],
        handler: () => "I have strong experience with AWS services like Lambda, API Gateway, Cognito, DynamoDB, and CloudFormation.",
      },

      {
        id: "architecture",
        keywords: ["architecture", "system design", "design"],
        handler: () => "I design scalable, secure, and high-performance systems using modern cloud and microservice architectures."
      },

      {
        id: "leadership",
        keywords: ["team lead", "leadership", "management"],
        handler: () => "I've led offshore teams, conducted code reviews, mentored developers, and ensured timely delivery."
      },

      {
        id: "testing",
        keywords: ["testing", "unit tests", "jest"],
        handler: () => "I follow test-driven development and have achieved up to 90% test coverage using Jest and related tools."
      },

      {
        id: "performance",
        keywords: ["performance", "optimization", "speed"],
        handler: () => "I focus heavily on performance optimization, reducing load times and improving application stability."
      },

      {
        id: "experience",
        keywords: ["experience", "companies", "worked", "career"],
        handler: () =>
          `I have worked at ${this._resumeData?.employmentHistory
            .slice(1)
            .map(e => `${e.organisation} (${e.shortPeriod})`)
            .join(", ")}.`,
      },

      {
        id: "projects",
        keywords: ["projects", "project", "worked"],
        handler: () =>
          `I have worked on projects like ${this._resumeData?.projects
            .slice(0, 3)
            .map(p => p.projectName)
            .join(", ")} and more.`,
      },

      {
        id: "currentProject",
        keywords: ["current project", "latest project", this._resumeData?.projects?.[0]?.projectName?.toLowerCase()!],
        handler: () => {
          const p = this._resumeData?.projects?.[0];
          return `My current project is ${p?.projectName}: ${p?.projectOverview}`;
        },
      },

      {
        id: "education",
        keywords: ["education", "degree", "college"],
        handler: () => {
          const e = this._resumeData?.education?.[0];
          return `I completed ${e?.exam} with ${e?.result}.`;
        },
      },

      {
        id: "certifications",
        keywords: ["certification", "certified"],
        handler: () =>
          `My certifications include: ${this._resumeData?.certifications?.join(", ")}.`,
      },

      {
        id: "contact",
        keywords: ["contact", "phone", "mobile"],
        handler: () =>
          `You can call me at ${this._resumeData?.aboutMe?.contactInfo?.Mobile}.`,
      },

      {
        id: "email",
        keywords: ["email"],
        handler: () =>
          `You can email me at ${this._resumeData?.aboutMe?.contactInfo?.Email}.`,
      },

      {
        id: "hire",
        keywords: ["hire", "hiring", "available", "job", "opportunity"],
        handler: () => "I'm open to exciting full-stack and system design opportunities."
      }
    ]
  }

  getBestResponse(input: string) {
    const text = input.toLowerCase();

    let best: BestResponse = { score: 0, response: null };

    for (const intent of this.intents) {
      const score = this.calculateScore(text, intent.keywords);
      if (score > best.score) {
        best = { score, response: intent.handler };
      }
    }

    return best.response
      ? best.response()
      : "Sorry, I didn’t understand that. You can ask me about my skills, experience, or current project.";;
  }

  private calculateScore(text: string, keywords: string[]) {
    let score = 0;

    for (const keyword of keywords) {
      if (text.includes(keyword)) {
        score += keyword.split(" ").length;
      }
    }

    return score;
  }

  private getExperienceYears(): string {
    const startDate = new Date(2013, 11, 10); // Month is 0-based (Dec = 11)
    const today = new Date();

    let years = today.getFullYear() - startDate.getFullYear();

    // Adjust if current date is before the anniversary
    const hasCompletedYear =
      today.getMonth() > startDate.getMonth() ||
      (today.getMonth() === startDate.getMonth() &&
        today.getDate() >= startDate.getDate());

    if (!hasCompletedYear) {
      years--;
    }

    return `${years}+`;
  }

}

export const qnaService = new QnAService();
